import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  TextField,
  Tooltip,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import * as pdfjsLib from "pdfjs-dist";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PropTypes from "prop-types";
import { encrypt } from "../../Encrypdecryp/Crypt";
import JsonEditor from "../../components/JsonEditor";
import ProgressBar from "./ProgressBar";
import DragAndDrop from "./DragAndDrop";
import FileCard from "./FileCard";
import PdfPreview from "../../components/PdfPreview";
import "./NewTransaction.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error("API_BASE_URL is not defined in .env");
}

const BANK_ACCOUNTS_ENDPOINT = `${API_BASE_URL}/exiting_bankaccounts`;

const paymentMethods = [
  "Advance Payment",
  "Letter of Credit",
  "Open Account",
  "Documentary Collection",
  "Bank Contract",
  "Consignment Basis",
];

const NewTransaction = ({
  userRole,
  onSaveDocument,
  onCompleteTransaction,
  onCategoryChange,
  uploadedFiles,
  setUploadedFiles,
  selectedFileIndex,
  setSelectedFileIndex,
  selectedFileType,
  setSelectedFileType,
  pdfUrl,
  setPdfUrl,
  extractedData,
  setExtractedData,
  originalExtractedData,
  setOriginalExtractedData,
  documentId,
  setDocumentId,
  loading,
  setLoading,
  error,
  setError,
  pollingProgress,
  setPollingProgress,
  pollingReferenceIds,
  setPollingReferenceIds,
  progressComplete,
  setProgressComplete,
  docType,
  setDocType,
  isDataChanged,
  setIsDataChanged,
  selectedCategory,
}) => {
  const [tradeCategory, setTradeCategory] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openBankModal, setOpenBankModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);
  const [bankSearchQuery, setBankSearchQuery] = useState("");
  const [filteredBankAccounts, setFilteredBankAccounts] = useState([]);
  const [bankAccountsError, setBankAccountsError] = useState(null);
  const [deletedFileNames, setDeletedFileNames] = useState(new Set());
  const [transactionSetId, setTransactionSetId] = useState(null);
  const fileInputRef = useRef(null);

  const selectedBankAccount = bankAccounts.find(
    (account) => account.id === selectedBankAccountId
  );
  const bankAccountDisplay = selectedBankAccount
    ? `${selectedBankAccount.finalized_data?.fullName ?? "Unnamed Account"} (${
        selectedBankAccount.finalized_data?.titleOfAccount ?? "N/A"
      })`
    : "";

  useEffect(() => {
    setOpenModal(selectedCategory === "New Transaction");
  }, [selectedCategory]);

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setProgressComplete(false);
      setPdfUrl(null);
      setPollingReferenceIds(new Set());
      setPollingProgress(0);
      // DO NOT reset transactionSetId - keep it for future uploads in the same transaction
    }
  }, [
    uploadedFiles.length,
    setPdfUrl,
    setPollingReferenceIds,
    setPollingProgress,
    setProgressComplete,
  ]);

  const handleTradeCategoryChange = (category) => {
    setTradeCategory(category === tradeCategory ? "" : category);
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  const fetchBankAccountsWithRetry = async (
    token,
    retries = 3,
    delay = 1000
  ) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await axios.get(BANK_ACCOUNTS_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise((resolve) =>
          setTimeout(resolve, delay * Math.pow(2, i))
        );
      }
    }
  };

  const handleModalConfirm = async () => {
    if (!tradeCategory || !paymentMethod) {
      toast.warn("Please select trade category and payment method");
      return;
    }
    // All payment methods now require bank account selection
    setOpenModal(false);
    setOpenBankModal(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await fetchBankAccountsWithRetry(token);
      const accounts = response.data || [];
      if (!Array.isArray(accounts)) {
        throw new Error("Invalid response format: Expected an array of bank accounts.");
      }
      setBankAccounts(accounts);
      setFilteredBankAccounts(accounts);
      setBankAccountsError(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Network Error";
      setBankAccountsError(`Failed to fetch bank accounts: ${errorMessage}`);
      setBankAccounts([]);
      setFilteredBankAccounts([]);
      toast.error(`Failed to fetch bank accounts: ${errorMessage}`);
    }
  };

  const handleBankModalConfirm = () => {
    if (!selectedBankAccountId) {
      toast.warn("Please select a bank account");
      return;
    }
    setOpenBankModal(false);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setTradeCategory("");
    setPaymentMethod("");
    onCategoryChange("");
  };

  const handleBankModalClose = () => {
    setOpenBankModal(false);
    setTradeCategory("");
    setPaymentMethod("");
    setBankAccounts([]);
    setFilteredBankAccounts([]);
    setBankSearchQuery("");
    setSelectedBankAccountId(null);
    setBankAccountsError(null);
    onCategoryChange("");
  };

  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setBankSearchQuery(query);
    if (!query) {
      setFilteredBankAccounts(bankAccounts);
    } else {
      const filtered = bankAccounts.filter(
        (account) =>
          (account.finalized_data?.fullName?.toLowerCase() ?? "").includes(query) ||
          (account.finalized_data?.titleOfAccount?.toLowerCase() ?? "").includes(query)
      );
      setFilteredBankAccounts(filtered);
    }
  };

  const handleBankAccountSelect = (id, accountName) => {
    setSelectedBankAccountId(id);
    setBankSearchQuery(accountName);
  };

  const handleBankAccountDoubleClick = (id, accountName) => {
    setSelectedBankAccountId(id);
    setBankSearchQuery(accountName);
    setOpenBankModal(false);
  };

  const updatedTableRows = () => {
    return filteredBankAccounts.map((account) => (
      <TableRow
        key={account.id}
        className={selectedBankAccountId === account.id ? "selected-row" : ""}
        onClick={() =>
          handleBankAccountSelect(
            account.id,
            account.finalized_data?.fullName ||
              `Unnamed Account (ID: ${account.id})`
          )
        }
        onDoubleClick={() =>
          handleBankAccountDoubleClick(
            account.id,
            account.finalized_data?.fullName ||
              `Unnamed Account (ID: ${account.id})`
          )
        }
        style={{ cursor: "pointer" }}
      >
        <TableCell>
          {account.finalized_data?.fullName ?? "Unnamed Account"}
        </TableCell>
        <TableCell>{account.finalized_data?.titleOfAccount ?? "N/A"}</TableCell>
      </TableRow>
    ));
  };

  const handleFileChange = async (selectedFiles) => {
    if (
      !selectedFiles.length ||
      !Array.from(selectedFiles).every(
        (file) => file.type === "application/pdf"
      )
    ) {
      toast.error("Please upload only PDF files");
      return;
    }
    if (selectedFiles.length > 10) {
      toast.warn("You can upload up to 10 files at a time");
      return;
    }
    if (!paymentMethod) {
      toast.warn("Please select a payment method first");
      return;
    }
    const existingFileNames = uploadedFiles.map((f) => f.name.toLowerCase());
    const newFileNames = Array.from(selectedFiles).map((f) =>
      f.name.toLowerCase()
    );
    const duplicates = newFileNames.filter((name) =>
      existingFileNames.includes(name)
    );
    if (duplicates.length > 0) {
      toast.warn(`Files already uploaded: ${duplicates.join(", ")}`);
      return;
    }

    setProgressComplete(false);
    setPollingProgress(0);
    setLoading(true);
    setError(null);
    
    try {
      const response = await extractPdf(transactionSetId, selectedFiles);
      setTransactionSetId(response.transaction_set_id || transactionSetId);
      setDocumentId(
        response.document_ids ? response.document_ids[0] : response.document_id
      );
      setPollingReferenceIds(
        (prev) => new Set([...prev, response.reference_id])
      );

      const updatedUploadedFiles = await Promise.all(
        Array.from(selectedFiles).map(async (file, index) => {
          const pdfUrl = URL.createObjectURL(file);
          const reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = async () => {
              const fileData = reader.result;
              const thumbnail = await generateThumbnail(
                fileData,
                response.reference_id,
                file.name
              );
              const fileId = response.document_ids
                ? `${response.reference_id}-${index}`
                : `temp-${response.reference_id}-${index}`;
              resolve({
                id: fileId,
                name: file.name,
                docType,
                pdfUrl,
                fileData,
                initialData: null,
                thumbnail,
                documentId: response.document_ids
                  ? response.document_ids[index]
                  : response.document_id,
                transaction_set_id:
                  response.transaction_set_id || transactionSetId,
                reference_id: response.reference_id,
                isSaved: false,
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      setUploadedFiles((prev) => {
        const newFiles = [...prev, ...updatedUploadedFiles];
        const newFileIndex = prev.length;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(newFileIndex);
        setPdfUrl(updatedUploadedFiles[0].pdfUrl);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(updatedUploadedFiles[0].documentId || null);
        return newFiles;
      });

      setLoading(false);
    } catch (err) {
      let errorMessage = "Failed to process PDFs";
      if (err.response?.data?.detail) {
        errorMessage = Array.isArray(err.response.data.detail)
          ? err.response.data.detail.map((d) => d.msg).join("; ")
          : err.response.data.detail;
      }
      
      // If transaction set not found, reset the transactionSetId and retry
      if (errorMessage.includes("Transaction set not found") && transactionSetId) {
        console.log("Transaction set not found, resetting and retrying...");
        setTransactionSetId(null);
        toast.warn("Previous transaction expired. Creating new transaction...");
        
        // Retry without transaction set ID
        try {
          const response = await extractPdf(null, selectedFiles);
          setTransactionSetId(response.transaction_set_id);
          setDocumentId(
            response.document_ids ? response.document_ids[0] : response.document_id
          );
          setPollingReferenceIds(
            (prev) => new Set([...prev, response.reference_id])
          );

          const updatedUploadedFiles = await Promise.all(
            Array.from(selectedFiles).map(async (file, index) => {
              const pdfUrl = URL.createObjectURL(file);
              const reader = new FileReader();
              return new Promise((resolve) => {
                reader.onload = async () => {
                  const fileData = reader.result;
                  const thumbnail = await generateThumbnail(
                    fileData,
                    response.reference_id,
                    file.name
                  );
                  const fileId = response.document_ids
                    ? `${response.reference_id}-${index}`
                    : `temp-${response.reference_id}-${index}`;
                  resolve({
                    id: fileId,
                    name: file.name,
                    docType,
                    pdfUrl,
                    fileData,
                    initialData: null,
                    thumbnail,
                    documentId: response.document_ids
                      ? response.document_ids[index]
                      : response.document_id,
                    transaction_set_id: response.transaction_set_id,
                    reference_id: response.reference_id,
                    isSaved: false,
                  });
                };
                reader.readAsDataURL(file);
              });
            })
          );

          setUploadedFiles((prev) => {
            const newFiles = [...prev, ...updatedUploadedFiles];
            const newFileIndex = prev.length;
            setSelectedFileType("uploaded");
            setSelectedFileIndex(newFileIndex);
            setPdfUrl(updatedUploadedFiles[0].pdfUrl);
            setExtractedData(null);
            setOriginalExtractedData(null);
            setDocumentId(updatedUploadedFiles[0].documentId || null);
            return newFiles;
          });

          setLoading(false);
          return; // Success, exit the function
        } catch (retryErr) {
          errorMessage = "Failed to create new transaction: " + 
            (retryErr.response?.data?.detail 
              ? Array.isArray(retryErr.response.data.detail)
                ? retryErr.response.data.detail.map((d) => d.msg).join("; ")
                : retryErr.response.data.detail
              : retryErr.message);
        }
      }
      
      setError(errorMessage);
      setLoading(false);
      toast.error(errorMessage);
    }
  };

  const extractPdf = async (transactionId, files) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("pdf_files", file));
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("No authentication token found.");
    let url = `${API_BASE_URL}/documents/extract`;
    const params = new URLSearchParams();
    if (transactionId) {
      params.append("transaction_set_id", transactionId);
    } else {
      if (!paymentMethod)
        throw new Error("Payment method is required for new transactions.");
      params.append("payment_type", paymentMethod);
      // All payment methods now require bank account selection
      if (selectedBankAccountId) {
        params.append("bank_account_opening_id", selectedBankAccountId);
      }
    }
    url += `?${params.toString()}`;
    const response = await axios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000,
    });
    return response.data;
  };

  const generateThumbnail = async (fileData, referenceId, fileName) => {
    try {
      if (referenceId && fileName) {
        const statusResponse = await axios.get(
          `${API_BASE_URL}/documents/status/${referenceId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            timeout: 10000,
          }
        );
        const document = statusResponse.data.documents?.find(
          (doc) => doc.pdf_file_name === fileName
        );
        if (document && document.thumbnail_path) {
          return `${API_BASE_URL}/thumbnails/${document.thumbnail_path}`;
        }
      }
      const pdf = await pdfjsLib.getDocument({
        data: atob(fileData.split(",")[1]),
      }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext("2d");
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL("image/jpeg");
    } catch (error) {
      return "/default_thumb.jpg";
    }
  };

  const handleFileSelect = (index) => {
    if (isDataChanged) {
      toast.warn("Save or cancel changes before switching documents");
      return;
    }

    if (index === selectedFileIndex) {
      return;
    }

    setSelectedFileType("uploaded");
    setSelectedFileIndex(index);
    setPdfUrl(uploadedFiles[index].pdfUrl);
    setExtractedData(uploadedFiles[index].initialData ?? null);
    setOriginalExtractedData(uploadedFiles[index].initialData ?? null);
    setDocumentId(uploadedFiles[index].documentId ?? null);
  };

  const handleDeleteUploadedFile = async (fileId, fileName, index) => {
    if (isDataChanged) {
      toast.warn("Save or cancel changes before deleting");
      return;
    }

    const file = uploadedFiles[index];
    try {
      // Always trigger delete endpoint if documentId and transaction_set_id exist
      if (file.documentId && file.transaction_set_id) {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        
        await axios.delete(
          `${API_BASE_URL}/delete/transaction/${file.transaction_set_id}/document/${file.documentId}/`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
      }
      
      const updatedUploadedFiles = uploadedFiles.filter((f, i) => i !== index);
      setUploadedFiles(updatedUploadedFiles);
      setDeletedFileNames((prev) => new Set([...prev, fileName.toLowerCase()]));
      
      if (updatedUploadedFiles.length === 0) {
        // Keep transactionSetId to allow uploading more files to the same transaction
        setSelectedFileIndex(null);
        setSelectedFileType(null);
        setPdfUrl(null);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(null);
        setProgressComplete(false);
        setPollingReferenceIds(new Set());
        setPollingProgress(0);
        // DO NOT reset transactionSetId here - it's preserved for reuse
      } else if (selectedFileIndex === index) {
        const newIndex =
          index >= updatedUploadedFiles.length
            ? updatedUploadedFiles.length - 1
            : index;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(newIndex);
        setPdfUrl(updatedUploadedFiles[newIndex].pdfUrl);
        setExtractedData(updatedUploadedFiles[newIndex].initialData ?? null);
        setOriginalExtractedData(updatedUploadedFiles[newIndex].initialData ?? null);
        setDocumentId(updatedUploadedFiles[newIndex].documentId ?? null);
      } else if (selectedFileIndex !== null && index < selectedFileIndex) {
        setSelectedFileIndex(selectedFileIndex - 1);
      }
      
      toast.success("File deleted successfully");
    } catch (error) {
      toast.error(`Failed to delete: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSaveDocumentLocal = async (file, index) => {
    if (!file.initialData || !file.documentId) {
      toast.warn("Please wait for document processing to complete");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const encryptedToken = encrypt(token);
      const dataToSend =
        selectedFileIndex === index && extractedData
          ? extractedData
          : file.initialData;
      if (!dataToSend || typeof dataToSend !== "object") {
        throw new Error("Invalid finalized_data: Expected an object.");
      }
      const requestBody = {
        transaction_set_id: String(file.transaction_set_id),
        document_id: String(file.documentId),
        finalized_data: dataToSend,
        token: encryptedToken,
        payment_type: paymentMethod,
      };
      
      await axios.post(
        `${API_BASE_URL}/documents/confirm`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000,
        }
      );
      
      const updatedFiles = uploadedFiles.map((f, i) =>
        i === index
          ? { ...f, isSaved: true, savedAt: new Date().toISOString() }
          : f
      );
      setUploadedFiles(updatedFiles);
      if (selectedFileIndex === index) {
        setExtractedData(dataToSend);
        setOriginalExtractedData(dataToSend);
        setIsDataChanged(false);
      }
      onSaveDocument(file, index);
      toast.success("Document saved successfully");
    } catch (error) {
      const errorMessage = error.response?.data?.detail
        ? Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map((d) => d.msg).join("; ")
          : error.response.data.detail
        : error.response?.data?.message || error.message;
      toast.error(`Failed to save: ${errorMessage}`);
    }
  };

  const resetPage = () => {
    console.log("Resetting NewTransaction page state");
    setTradeCategory("");
    setPaymentMethod("");
    setOpenModal(false);
    setOpenBankModal(false);
    setBankAccounts([]);
    setSelectedBankAccountId(null);
    setBankSearchQuery("");
    setFilteredBankAccounts([]);
    setBankAccountsError(null);
    setDeletedFileNames(new Set());
    setTransactionSetId(null); // Only reset transactionSetId here on Complete Transaction
    setUploadedFiles([]);
    setSelectedFileIndex(null);
    setSelectedFileType(null);
    setPdfUrl(null);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setPollingReferenceIds(new Set());
    setPollingProgress(0);
    setProgressComplete(false);
    setIsDataChanged(false);
    onCategoryChange("");
    onCompleteTransaction();
  };

  const handleCompleteTransactionLocal = async () => {
    if (!transactionSetId) {
      toast.warn("No transaction ID available");
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      
      console.log(`Attempting to complete transaction ${transactionSetId}`);
      
      const response = await axios.post(
        `${API_BASE_URL}/complete/transaction/${transactionSetId}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
      );
      
      console.log(`Complete transaction response:`, response.data);
      
      if (response.data.message === "Transaction set is not empty and cannot be deleted") {
        console.log(`Triggering validate-all endpoint for transaction ${transactionSetId}`);
        try {
          const validateResponse = await axios.post(
            `${API_BASE_URL}/documents/validate-all`,
            { transaction_set_id: transactionSetId },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
          );
          console.log(`Validate-all response:`, validateResponse.data);
          toast.info("Validation of all documents triggered", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "toast-success",
          });
          resetPage();
        } catch (validateError) {
          const validateErrorMessage = validateError.response?.data?.detail
            ? Array.isArray(validateError.response.data.detail)
              ? validateError.response.data.detail.map((d) => d.msg).join("; ")
              : validateError.response.data.detail
            : validateError.response?.data?.message || validateError.message;
          console.error(`Validate-all error:`, validateErrorMessage);
          toast.error(`Failed to trigger validation: ${validateErrorMessage}`, {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "toast-error",
          });
        }
        return;
      }
      
      toast.success("Transaction completed successfully", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
      
      resetPage();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      console.error(`Complete transaction error:`, errorMessage);
      toast.error(`Failed to complete transaction: ${errorMessage}`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
    }
  };

  const handleJsonChange = (newData) => {
    if (!newData || typeof newData !== "object") {
      return;
    }
    setExtractedData(newData);
    const isDataUnchanged =
      JSON.stringify(newData) === JSON.stringify(originalExtractedData);
    setIsDataChanged(!isDataUnchanged);
    if (selectedFileIndex !== null) {
      setUploadedFiles((prev) =>
        prev.map((file, index) =>
          index === selectedFileIndex
            ? { ...file, initialData: newData, isSaved: false }
            : file
        )
      );
    }
  };

  const handleCancelUpdate = () => {
    setExtractedData(originalExtractedData);
    setIsDataChanged(false);
    if (selectedFileIndex !== null && uploadedFiles[selectedFileIndex]) {
      setUploadedFiles((prev) =>
        prev.map((file, index) =>
          index === selectedFileIndex
            ? { ...file, initialData: originalExtractedData }
            : file
        )
      );
    }
  };

  useEffect(() => {
    const pollingIntervals = {};
    const POLLING_TIMEOUT = 60000;
    const startTimes = {};

    const pollStatus = async (referenceId) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        const response = await axios.get(
          `${API_BASE_URL}/documents/status/${referenceId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
          }
        );

        const filesForReferenceId = uploadedFiles.filter(
          (file) => file.reference_id === referenceId
        );
        const totalFiles = filesForReferenceId.length;
        let processedFiles = 0;

        const updatedUploadedFiles = uploadedFiles.map((file) => {
          if (file.reference_id === referenceId) {
            const document = response.data.documents?.find(
              (doc) => doc.pdf_file_name === file.name
            );
            if (document) {
              if (document.initial_data) processedFiles++;
              return {
                ...file,
                initialData: document.initial_data ?? file.initialData,
                documentId: document.document_id ?? file.documentId,
                thumbnail: document.thumbnail_path
                  ? `${API_BASE_URL}/thumbnails/${document.thumbnail_path}`
                  : file.thumbnail ?? "/default_thumb.jpg",
                transaction_set_id:
                  document.transaction_set_id?.toString() ??
                  file.transaction_set_id,
              };
            }
          }
          return file;
        });

        setUploadedFiles(updatedUploadedFiles);

        const progress =
          totalFiles > 0 ? (processedFiles / totalFiles) * 100 : 0;
        setPollingProgress(progress);

        if (
          selectedFileIndex !== null &&
          updatedUploadedFiles[selectedFileIndex]?.reference_id === referenceId
        ) {
          const newInitialData =
            updatedUploadedFiles[selectedFileIndex].initialData ?? null;
          setExtractedData(newInitialData);
          setOriginalExtractedData(newInitialData);
          setDocumentId(
            updatedUploadedFiles[selectedFileIndex].documentId ?? null
          );
        }

        const allFilesProcessed = filesForReferenceId.every(
          (file) => file.initialData
        );
        const elapsedTime =
          Date.now() - (startTimes[referenceId] || Date.now());
        if (allFilesProcessed || elapsedTime > POLLING_TIMEOUT) {
          clearInterval(pollingIntervals[referenceId]);
          setPollingReferenceIds((prev) => {
            const newSet = new Set([...prev]);
            newSet.delete(referenceId);
            return newSet;
          });
          if (allFilesProcessed && !progressComplete) {
            setProgressComplete(true);
            setTimeout(() => {
              setProgressComplete(false);
            }, 3000);
            toast.success("Documents processed successfully");
          } else if (elapsedTime > POLLING_TIMEOUT) {
            toast.error("Document processing timed out");
          }
        }
      } catch (error) {
        const elapsedTime =
          Date.now() - (startTimes[referenceId] || Date.now());
        if (elapsedTime > POLLING_TIMEOUT) {
          clearInterval(pollingIntervals[referenceId]);
          setPollingReferenceIds((prev) => {
            const newSet = new Set([...prev]);
            newSet.delete(referenceId);
            return newSet;
          });
          setPollingProgress(0);
          setProgressComplete(false);
        }
      }
    };

    pollingReferenceIds.forEach((referenceId) => {
      if (!startTimes[referenceId]) startTimes[referenceId] = Date.now();
      pollingIntervals[referenceId] = setInterval(
        () => pollStatus(referenceId),
        5000
      );
    });

    return () =>
      Object.values(pollingIntervals).forEach((interval) =>
        clearInterval(interval)
      );
  }, [
    pollingReferenceIds,
    uploadedFiles,
    selectedFileIndex,
    progressComplete,
    setPollingProgress,
    setPollingReferenceIds,
    setProgressComplete,
    setUploadedFiles,
    setExtractedData,
    setOriginalExtractedData,
    setDocumentId,
  ]);

  const isDragAndDropDisabled =
    pollingReferenceIds.size > 0 ||
    !paymentMethod ||
    !selectedBankAccountId; // All payment methods require bank account selection

  const areAllFilesSaved =
    uploadedFiles.length === 0 || uploadedFiles.every((file) => file.isSaved);

  // Reusable Disabled Icon Component
  const DisabledIcon = () => (
    <Tooltip title="Not available">
      <LockOutlinedIcon
        sx={{
          fontSize: 16,
          ml: 1,
          color: "action.disabled",
          verticalAlign: "middle",
        }}
      />
    </Tooltip>
  );

  return (
    <Box className="card" sx={{ width: "100%", mb: 3, p: 3 }}>
     <Typography variant="h6" gutterBottom className="newt-title">
        New Transaction
      </Typography>
    <Box className="newt-container">
      {paymentMethod && (
        <Box className="newt-selection-display">
          <Typography variant="subtitle1">
            <strong>Payment Method:</strong> {paymentMethod}
          </Typography>
          {selectedBankAccountId && (
            <Typography variant="subtitle1" className="newt-bank-display">
              <strong>Bank Account:</strong> {bankAccountDisplay}
            </Typography>
          )}
        </Box>
      )}
      
      <Dialog 
        open={openModal} 
        onClose={handleModalClose} 
        className="newt-modal" 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Select Transaction Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Box className="newt-checkbox-group">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tradeCategory === "Import"}
                    onChange={() => handleTradeCategoryChange("Import")}
                  />
                }
                label="Import"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tradeCategory === "Export"}
                    onChange={() => handleTradeCategoryChange("Export")}
                  />
                }
                label="Export"
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentMethod}
                label="Payment Method"
                onChange={handlePaymentMethodChange}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} className="newt-button newt-button-primary"
          variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleModalConfirm}
            className="newt-button newt-button-primary"
            variant="contained"
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={openBankModal} 
        onClose={handleBankModalClose} 
        className="newt-modal" 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Select Bank Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <input
              type="text"
              placeholder="Search Bank Account"
              value={bankSearchQuery}
              onChange={handleSearchChange}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'var(--card-background)',
                color: 'var(--text-color)',
                outline: 'none',
                fontFamily: 'var(--font-family-base)',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
              }}
            />
            {bankAccountsError && (
              <Typography color="error" variant="body2" className="newt-error-text">
                {bankAccountsError}
              </Typography>
            )}
            {filteredBankAccounts.length > 0 && (
              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Account</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{updatedTableRows()}</TableBody>
                </Table>
              </Box>
            )}
            {filteredBankAccounts.length === 0 && bankSearchQuery && (
              <Typography variant="body2" className="newt-placeholder-text">
                No bank accounts found
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBankModalClose} className="newt-button newt-button-primary"
          variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBankModalConfirm}
            className="newt-button newt-button-primary"
            variant="contained"
            disabled={!selectedBankAccountId}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      
      <ProgressBar
        progress={pollingProgress}
        isProcessing={pdfUrl && pollingReferenceIds.size > 0}
        hasFiles={uploadedFiles.length > 0}
      />
      
      <DragAndDrop
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
        disabled={isDragAndDropDisabled}
      />
      
      <div className="newt-content">
        <div className="newt-preview-container">
          <div className="newt-preview-section">
            <Typography variant="subtitle1">PDF Preview</Typography>
            {paymentMethod ? (
              <PdfPreview pdfUrl={pdfUrl} />
            ) : (
              <Typography className="newt-placeholder-text">
                Select payment method to start
              </Typography>
            )}
          </div>
        </div>
        
        <div className="newt-summary-container">
          <div className="newt-summary-section">
            <Typography variant="subtitle1">Extracted Data</Typography>
            {paymentMethod ? (
              loading ? (
                <Box className="newt-loading-container">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" className="newt-error-text">{error}</Typography>
              ) : extractedData ? (
                <>
                  <JsonEditor
                    jsonData={extractedData}
                    onEditChange={handleJsonChange}
                    onChange={handleJsonChange}
                    isNew={true}
                    isAdmin={userRole === "admin"}
                    transactionId={transactionSetId}
                    documentId={documentId}
                  />
                  {userRole === "admin" && isDataChanged && (
                    <Box className="newt-buttons">
                      <Button
                        variant="contained"
                        className="newt-button newt-button-success"
                        onClick={() =>
                          handleSaveDocumentLocal(
                            uploadedFiles[selectedFileIndex],
                            selectedFileIndex
                          )
                        }
                        disabled={!uploadedFiles[selectedFileIndex]}
                      >
                        Update
                      </Button>
                      <Button
                        variant="outlined"
                        className="newt-button newt-button-secondary"
                        onClick={handleCancelUpdate}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography className="newt-placeholder-text">
                  No data available for selected file
                </Typography>
              )
            ) : (
              <Typography className="newt-placeholder-text">
                Upload files to view extracted data
              </Typography>
            )}
          </div>
        </div>
      </div>
      
      <Box className="newt-uploaded-files">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 2 }} className="newt-files-grid">
          {uploadedFiles.map((file, index) => (
            <FileCard
              key={file.id}
              file={file}
              index={index}
              selectedFileIndex={selectedFileIndex}
              onSelect={() => handleFileSelect(index)}
              onDelete={() =>
                handleDeleteUploadedFile(file.id, file.name, index)
              }
              onSave={() => handleSaveDocumentLocal(file, index)}
              isSavedDisabled={() =>
                !file.initialData || !file.documentId || file.isSaved
              }
            />
          ))}
        </Box>
      </Box>
      
      <Box className="newt-buttons" sx={{ mt: 2, textAlign: "center" }}>
        <Button
          className="newt-button newt-button-primary"
          onClick={handleCompleteTransactionLocal}
          disabled={!transactionSetId || (uploadedFiles.length > 0 && !areAllFilesSaved)}
        >
          Complete Transaction
        </Button>
      </Box>
    </Box>
  </Box>
  );
};

NewTransaction.propTypes = {
  userRole: PropTypes.string.isRequired,
  onSaveDocument: PropTypes.func.isRequired,
  onCompleteTransaction: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  setUploadedFiles: PropTypes.func.isRequired,
  selectedFileIndex: PropTypes.number,
  setSelectedFileIndex: PropTypes.func.isRequired,
  selectedFileType: PropTypes.string,
  setSelectedFileType: PropTypes.func.isRequired,
  pdfUrl: PropTypes.string,
  setPdfUrl: PropTypes.func.isRequired,
  extractedData: PropTypes.object,
  setExtractedData: PropTypes.func.isRequired,
  originalExtractedData: PropTypes.object,
  setOriginalExtractedData: PropTypes.func.isRequired,
  documentId: PropTypes.string,
  setDocumentId: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  pollingProgress: PropTypes.number.isRequired,
  setPollingProgress: PropTypes.func.isRequired,
  pollingReferenceIds: PropTypes.instanceOf(Set).isRequired,
  setPollingReferenceIds: PropTypes.func.isRequired,
  progressComplete: PropTypes.bool.isRequired,
  setProgressComplete: PropTypes.func.isRequired,
  docType: PropTypes.string.isRequired,
  setDocType: PropTypes.func.isRequired,
  isDataChanged: PropTypes.bool.isRequired,
  setIsDataChanged: PropTypes.func.isRequired,  
  selectedCategory: PropTypes.string.isRequired,
};

export default NewTransaction;